import { InputAmount, MMButton } from '@components'
import { CACHE_KEYS } from '@constants'
import cn from '@integrations/classnames'
import { v4 as uuid } from '@integrations/ids'
import toast from '@integrations/notifications'
import { useContext, useState } from '@integrations/react'
import { usePayoutInitiateMutation, usePayoutPreviewMutation } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, TransactionViewPortal } from 'mmfintech-backend-api/types'
import { OtpContext, tr, useFormValues } from 'mmfintech-commons'
import { ErrorDisplay, LabelValue } from 'mmfintech-portal-commons'
import {
  TransactionActionModalAmount,
  TransactionActionModalInfo,
  TransactionActionModalSurface
} from '../TransactionActionModalSurface'
import PayoutPreview from './PayoutPreview'

interface Props {
  transaction: TransactionViewPortal
  reload: () => void
}

const STEPS = {
  PREVIEW: 1,
  PAYOUT: 2
} as const

type Step = (typeof STEPS)[keyof typeof STEPS]

function PayoutModal({ transaction, reload }: Props) {
  const { setOtpOnSuccess } = useContext(OtpContext)

  const { id, checkoutDetails, currency, paymentMethod } = transaction || {}
  const { customerName, customerEmail } = checkoutDetails || {}

  // const paymentOption: PaymentOption = {
  //   status: 'AVAILABLE',
  //   minAmount: 0,
  //   maxAmount: amount
  // }

  const [payoutPreview, { error: payoutPreviewError, isLoading: payoutPreviewFetching, data: payoutPreviewData }] =
    usePayoutPreviewMutation({
      fixedCacheKey: CACHE_KEYS.PAYOUT_PREVIEW
    })
  const [payoutInit, { error: payoutInitError, isLoading: payoutInitFetching }] = usePayoutInitiateMutation({
    fixedCacheKey: CACHE_KEYS.PAYOUT_INIT
  })

  function isFormDataValid() {
    // const test = checkAmountLimits(fixFloat(formValues.getValue('amount')), paymentOption)
    // if (!test.valid) {
    //   formValues.setError('amount', translateError(test), test)
    //   return false
    // }
    return true
  }
  const [step, setStep] = useState<Step>(STEPS.PREVIEW)

  const formValues = useFormValues({
    amount: { required: true, validation: 'float', value: transaction?.amount.toString() }
  })

  async function handlePreview(payload: any) {
    try {
      await payoutPreview(payload).unwrap()
      setStep(STEPS.PAYOUT)
    } catch (_err) {}
  }

  async function handleRefundInit(payload: any) {
    setOtpOnSuccess(() => () => {
      toast.success(tr('FRONTEND.TRANSACTIONS.PAYOUT_SUCCESS', 'Payout initiated'))
      reload()
    })
    try {
      await payoutInit(payload).unwrap()
    } catch (_err) {}
  }

  function handleSubmit() {
    if (isFormDataValid()) {
      const payload = {
        payoutForeignTransactionId: uuid(),
        originalTransactionId: transaction.id,
        amount: formValues.getValue('amount')
      }

      switch (step) {
        case STEPS.PREVIEW:
          void handlePreview(payload)
          break

        case STEPS.PAYOUT:
          void handleRefundInit(payload)
          break

        default:
        // nothing
      }
    }
  }

  return (
    <TransactionActionModalSurface className={cn('payout-modal-wrapper')}>
      {step === STEPS.PREVIEW ? (
        <>
          <h3>{tr('FRONTEND.TRANSACTIONS.PAYOUT_MODAL.TITLE', 'Payout payment')}</h3>

          <TransactionActionModalInfo className={cn('info-container')}>
            <LabelValue
              label={tr('FRONTEND.TRANSACTIONS.PAYOUT_MODAL.TRANSACTION_ID', 'Transaction ID')}
              value={id}
              colon
              dataTest='transaction-id'
            />
            <LabelValue
              label={tr('FRONTEND.TRANSACTIONS.PAYOUT_MODAL.PAYMENT_METHOD', 'Payment method')}
              value={tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)}
              colon
              dataTest='payment-method'
            />
            <LabelValue
              label={tr('FRONTEND.TRANSACTIONS.PAYOUT_MODAL.CUSTOMER_NAME', 'Customer name')}
              value={customerName}
              colon
              dataTest='customer-name'
            />
            <LabelValue
              label={tr('FRONTEND.TRANSACTIONS.PAYOUT_MODAL.CUSTOMER_EMAIL', 'Customer email')}
              value={customerEmail}
              colon
              dataTest='customer-email'
            />
          </TransactionActionModalInfo>

          <TransactionActionModalAmount className={cn('amount-container')}>
            <InputAmount
              account={{ currencyCode: currency } as AccountBalanceResponse}
              error={formValues.getError('amount')}
              amount={formValues.getValue('amount')}
              setAmount={(value) => formValues.setValue('amount', value)}
              //paymentOption={paymentOption}
              autoFocus
            />
            <div>{currency}</div>
          </TransactionActionModalAmount>
        </>
      ) : null}

      {step === STEPS.PAYOUT ? (
        <PayoutPreview previewData={payoutPreviewData} checkoutDetails={checkoutDetails} />
      ) : null}

      <ErrorDisplay error={[payoutInitError, payoutPreviewError]} />

      <MMButton
        variant='primary'
        caption={
          step === STEPS.PREVIEW
            ? tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')
            : tr('FRONTEND.TRANSACTIONS.PAYOUT_MODAL.PAYOUT_BUTTON', 'Payout')
        }
        onClick={handleSubmit}
        disabled={formValues.getFloat('amount') <= 0}
        loading={payoutInitFetching || payoutPreviewFetching}
        fullWidth
      />
    </TransactionActionModalSurface>
  )
}

export default PayoutModal
